const $ = window.jQuery || window.$;
const { httpClient } = require('app_adyen/adyen/commons/httpClient');
const store = require('../../../../../../../config/store');
const helpers = require('../../../checkout/helpers');
const { PAYPAL } = require('../../../../../../../config/constants');
const { initializeCheckout } = require('app_adyen/adyen/express/initializeCheckout');
const { createTemporaryBasket } = require('app_adyen/adyen/commons');
const { isDromComAddress } = require('../../expressAddressValidation');

class Paypal {
  constructor(
    config,
    applicationInfo,
    adyenTranslations,
    isExpressPdp,
    initialAmount,
  ) {
    const {
      paypalReviewPageEnabled,
      returnUrl,
      basketAmount,
      makeExpressPaymentsCall,
      saveShopperData,
      makeExpressPaymentDetailsCall,
      showConfirmationAction,
      shippingMethodsUrl,
      selectShippingMethodUrl,
      saveExpressPaymentDataUrl,
    } = window;
    this.store = store;
    this.helpers = helpers;
    this.returnUrl = returnUrl;
    this.amount = initialAmount || JSON.parse(basketAmount);
    this.showPayButton = true;
    this.isExpress = true;
    this.isExpressPdp = isExpressPdp;
    this.userAction = paypalReviewPageEnabled ? 'continue' : null;
    this.makeExpressPaymentsCallUrl = makeExpressPaymentsCall;
    this.saveShopperDataUrl = saveShopperData;
    this.makeExpressPaymentDetailsCallUrl = makeExpressPaymentDetailsCall;
    this.showConfirmationAction = showConfirmationAction;
    this.shippingMethodsUrl = shippingMethodsUrl;
    this.selectShippingMethodUrl = selectShippingMethodUrl;
    this.saveExpressPaymentDataUrl = saveExpressPaymentDataUrl;
    this.applicationInfo = applicationInfo;
    this.translations = adyenTranslations;
    this.config = config;
  }

  static updateComponent(response, component) {
    if (response) {
      const { paymentData, status, errorMessage = '' } = response;
      if (!paymentData || status !== 'success') {
        throw new Error(errorMessage);
      }
      component.updatePaymentData(paymentData);
    } else {
      throw new Error(response?.errorMessage || '');
    }
    return false;
  }

  static onError() {
    $.spinner().stop();
  }

  async callPaymentFromComponent(state, component) {
    try {
      $.spinner().start();

      if (this.isExpressPdp) {
        const tempBasketResponse = await createTemporaryBasket();
        if (!tempBasketResponse?.temporaryBasketCreated) {
          component.handleError(new Error('Failed to create temporary basket'));
          return;
        }
      }

      const response = await httpClient({
        method: 'POST',
        url: this.makeExpressPaymentsCallUrl,
        data: {
          csrf_token: $('#adyen-token').val(),
          data: JSON.stringify({
            ...state.data,
            isExpressPdp: this.isExpressPdp,
          }),
        },
      });
      const { action } = response.fullResponse;
      if (action) {
        component.handleAction(action);
      } else {
        component.handleError(new Error('PayPal payment action was not returned'));
      }
    } catch (e) {
      component.handleError(e);
    }
  }

  async onAuthorized(data, actions) {
    try {
      const {
        authorizedEvent: { payer = {} },
        billingAddress,
        deliveryAddress,
      } = data;
      const shopperDetails = {
        shopperEmail: payer.email_address,
        telephoneNumber: payer.phone?.phone_number?.national_number,
        shopperName: {
          firstName: payer.name?.given_name,
          lastName: payer.name?.surname,
        },
        shippingAddress: deliveryAddress,
        billingAddress,
        isExpressPdp: this.isExpressPdp,
      };
      await httpClient({
        method: 'POST',
        url: this.saveShopperDataUrl,
        data: {
          data: JSON.stringify(shopperDetails),
        },
      });
      actions.resolve();
    } catch (e) {
      $.spinner().stop();
    }
  }

  async makeExpressPaymentDetailsCall(data) {
    try {
      const response = await httpClient({
        method: 'POST',
        url: this.makeExpressPaymentDetailsCallUrl,
        data: {
          data: JSON.stringify({ data, isExpressPdp: this.isExpressPdp }),
        },
      });
      this.helpers.createShowConfirmationForm(this.showConfirmationAction);
      this.helpers.setOrderFormData(response);
    } catch (e) {
      $.spinner().stop();
    }
  }

  async onShippingAddressChange(data, actions, component) {
    try {
      const { shippingAddress } = data;
      const currentPaymentData = component.paymentData;
      const siteCountryCode =
        window.countryCode || (window.locale || '').split('_').pop();

      if (!shippingAddress) {
        return actions.reject();
      }

      if (
        siteCountryCode &&
        siteCountryCode !== 'MN' &&
        shippingAddress.countryCode !== siteCountryCode
      ) {
        return actions.reject(
          data.errors.COUNTRY_ERROR || data.errors.ADDRESS_ERROR,
        );
      }

      if (
        isDromComAddress(shippingAddress.countryCode, shippingAddress.postalCode)
      ) {
        return actions.reject(data.errors.ADDRESS_ERROR);
      }

      const requestBody = {
        paymentMethodType: PAYPAL,
        currentPaymentData,
        isExpressPdp: this.isExpressPdp,
        address: {
          city: shippingAddress.city,
          country: shippingAddress.country,
          countryCode: shippingAddress.countryCode,
          stateCode: shippingAddress.state,
          postalCode: shippingAddress.postalCode,
        },
      };
      const response = await httpClient({
        method: 'POST',
        url: this.shippingMethodsUrl,
        data: {
          data: JSON.stringify(requestBody),
        },
      });
      Paypal.updateComponent(response, component);
      return null;
    } catch (e) {
      return actions.reject(data.errors.ADDRESS_ERROR);
    }
  }

  async onShippingOptionsChange(data, actions, component) {
    try {
      const { selectedShippingOption } = data;
      if (!selectedShippingOption) {
        return actions.reject();
      }
      const response = await httpClient({
        method: 'POST',
        url: this.selectShippingMethodUrl,
        data: {
          data: JSON.stringify({
            paymentMethodType: PAYPAL,
            currentPaymentData: component.paymentData,
            methodID: selectedShippingOption?.id,
            isExpressPdp: this.isExpressPdp,
          }),
        },
      });
      Paypal.updateComponent(response, component);
      return null;
    } catch (e) {
      return actions.reject(data.errors.METHOD_UNAVAILABLE);
    }
  }

  async onAdditionalDetails(state, component) {
    try {
      if (this.userAction) {
        const response = await httpClient({
          method: 'POST',
          url: this.saveExpressPaymentDataUrl,
          data: {
            data: JSON.stringify({
              ...state.data,
              isExpressPdp: this.isExpressPdp,
            }),
          },
        });
        if (response.success && response.redirectUrl) {
          window.location.href = response.redirectUrl;
        }
      } else {
        await this.makeExpressPaymentDetailsCall(state.data);
        document.querySelector('#additionalDetailsHidden').value =
          JSON.stringify(state.data);
        document.querySelector('#showConfirmationForm').submit();
      }
    } catch (e) {
      component.handleError(e);
      $.spinner().stop();
    }
  }

  getConfig() {
    return {
      configuration: this.config,
      showPayButton: this.showPayButton,
      returnUrl: this.returnUrl,
      amount: this.amount,
      isExpress: this.isExpress,
      ...(this.userAction ? { userAction: this.userAction } : {}),
      style: {
        color: 'white',
        layout: 'horizontal',
        disableMaxHeight: true,
      },
      onSubmit: this.callPaymentFromComponent.bind(this),
      onError: Paypal.onError,
      onAuthorized: this.onAuthorized.bind(this),
      onAdditionalDetails: this.onAdditionalDetails.bind(this),
      onShippingAddressChange: this.onShippingAddressChange.bind(this),
      onShippingOptionsChange: this.onShippingOptionsChange.bind(this),
      blockPayPalCreditButton: true,
      blockPayPalPayLaterButton: true,
      blockPayPalVenmoButton: true,
    };
  }

  async getComponent() {
    const checkout = await initializeCheckout(
      this.applicationInfo,
      this.translations,
    );
    const paypalConfig = this.getConfig();
    return window.AdyenWeb.createComponent(PAYPAL, checkout, paypalConfig);
  }
}

module.exports = Paypal;
